import { api } from "../config/api";
import { post, postAllowHttpError } from "../api";

export interface CasesSessionArgs {
  business_id: string | null;
  device_token: string | null;
  session_id: string | null;
}

export interface CaseItem {
  case_id: string | number;
  name?: string;
  case_id_exp?: string;
  description?: string;
  service?: string;
  created_date?: string;
  for_assesse_name?: string;
  for_assesse_pan?: string;
  case_curr_state?: string;
  client_name?: string;
  client_pan?: string;
  process?: string;
  process_id?: string;
  service_id?: string;
  client_id?: string | number;
  assessment_year?: string;
  is_draft?: boolean | string | number;
  author_name?: string;
  created_by?: string;
  owner?: { owner?: string } | null;
  current_step?: string;
  priority?: string;
  [key: string]: unknown;
}

export interface CasesPagination {
  total_cases: number;
  current_page?: number;
  total_pages?: number;
  page_size?: number;
  has_next?: boolean;
  has_previous?: boolean;
}

export interface GetCasesArgs extends CasesSessionArgs {
  page_number: number;
  page_size: number;
  assigned_users?: (string | number)[];
  case_statuses?: (string | number)[];
  tags?: (string | number)[];
  services?: (string | number)[];
}

export interface GetClientCasesArgs extends CasesSessionArgs {
  client_id: number | string;
  page_number: number;
  page_size: number;
}

export interface GetCasesResponse {
  success?: boolean;
  data?: {
    cases?: CaseItem[];
    pagination?: CasesPagination;
  };
  errors?: string;
}

function getCases(args: GetCasesArgs): Promise<GetCasesResponse> {
  const { business_id, device_token, session_id, page_number, page_size } = args;
  if (!business_id || !device_token || !session_id) {
    return Promise.reject("business_id, device_token and session_id are required");
  }
  const body: Record<string, unknown> = {
    business_id,
    device_token,
    session_id,
    page_number,
    page_size,
  };
  if (args.assigned_users !== undefined && args.assigned_users.length > 0) {
    body.assigned_users = args.assigned_users;
  }
  if (args.case_statuses !== undefined && args.case_statuses.length > 0) {
    body.case_statuses = args.case_statuses;
  }
  if (args.tags !== undefined && args.tags.length > 0) {
    body.tags = args.tags;
  }
  if (args.services !== undefined && args.services.length > 0) {
    body.services = args.services;
  }
  return post<GetCasesResponse>(api.GET_CASES_URL, body);
}

function getClientCases(args: GetClientCasesArgs): Promise<GetCasesResponse> {
  const { business_id, device_token, session_id, client_id, page_number, page_size } = args;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    client_id === null ||
    client_id === undefined
  ) {
    return Promise.reject("business_id, device_token, session_id and client_id are required");
  }
  return post<GetCasesResponse>(api.GET_CLIENT_CASES, {
    business_id,
    device_token,
    session_id,
    client_id,
    page_number,
    page_size,
  });
}

export interface CaseSearchLogItem {
  username?: string;
  name?: string;
  search_string?: string;
}

export type GetSearchLogPayload = CasesSessionArgs;

export interface GetSearchLogResponse {
  success?: boolean;
  data?: { case_searchlog?: CaseSearchLogItem[] };
  errors?: string | string[];
  error_code?: string | null;
}

function getSearchLog(payload: GetSearchLogPayload): Promise<GetSearchLogResponse> {
  const { business_id, device_token, session_id } = payload;
  if (!business_id || !device_token || !session_id) {
    return Promise.reject("business_id, device_token and session_id are required");
  }
  return post<GetSearchLogResponse>(api.GET_SEARCH_LOG, {
    device_token,
    session_id,
    business_id: typeof business_id === "number" ? business_id : Number(business_id),
  });
}

export interface SearchCasesPayload extends CasesSessionArgs {
  search_str: string;
  page_number: number;
  page_size: number;
  sort_by?: string;
  priority?: string;
  case_statuses?: (string | number)[];
  tags?: (string | number)[];
  services?: (string | number)[];
  clients?: (string | number)[];
  assign_to_all?: boolean;
  assigned_users?: (string | number)[];
  client_type?: string;
  from_date?: string;
  to_date?: string;
  assesment_year?: string;
}

export interface SearchResultClient {
  client_id?: number | string;
  client_name?: string;
  client_type?: string;
  client_state?: string;
  first_name?: string;
  middle_name?: string;
  surname?: string;
  org_name?: string | null;
  email?: string;
  country_code?: string;
  phone?: string;
  client_pan?: string;
  date_of_birth?: string;
  [key: string]: unknown;
}

export interface SearchResultMessage {
  message?: string;
  case_owner?: string;
  user?: string;
  case_name?: string;
  case_id?: string | number;
  case_curr_state?: string;
  document_type_id?: string | number;
  [key: string]: unknown;
}

export interface SearchCasesResponse {
  success?: boolean;
  data?: {
    search_results?: {
      cases?: CaseItem[];
      clients?: SearchResultClient[];
      messages?: SearchResultMessage[];
    };
    pagination?: {
      current_page?: number;
      total_pages?: number;
      total_cases?: number;
      page_size?: number;
      has_next?: boolean;
      has_previous?: boolean;
    };
  };
  meta?: Record<string, unknown>;
  errors?: string | string[];
  error_code?: string | null;
}

function searchCases(payload: SearchCasesPayload): Promise<SearchCasesResponse> {
  const { business_id, device_token, session_id, search_str, page_number, page_size } = payload;
  if (!business_id || !device_token || !session_id) {
    return Promise.reject("business_id, device_token and session_id are required");
  }
  const body: Record<string, unknown> = {
    business_id: typeof business_id === "number" ? business_id : Number(business_id),
    device_token,
    session_id,
    search_str: search_str ?? "",
    page_number,
    page_size,
    sort_by: payload.sort_by ?? "",
    priority: payload.priority ?? "",
    case_statuses: payload.case_statuses ?? [],
    tags: payload.tags ?? [],
    services: payload.services ?? [],
    clients: payload.clients ?? [],
    assign_to_all: payload.assign_to_all ?? false,
    assigned_users: payload.assigned_users ?? [],
    client_type: payload.client_type ?? "ALL",
    from_date: payload.from_date ?? "",
    to_date: payload.to_date ?? "",
    assesment_year: payload.assesment_year ?? "",
  };
  return post<SearchCasesResponse>(api.SEARCH_CASES, body);
}

export interface GetCaseDetailsPayload extends CasesSessionArgs {
  case_id: string;
}

export interface GetCaseDetailsResponse {
  success?: boolean;
  data?: { case_details?: Record<string, unknown> };
  errors?: string;
}

export interface CaseDetailData {
  case_id: string;
  case_id_exp?: string;
  name?: string;
  description?: string;
  service?: string;
  service_id?: string;
  process_name?: string;
  process_id?: string;
  assessment_year?: string;
  priority?: string;
  case_curr_state?: string;
  created_date?: string;
  author_name?: string;
  client_id?: string;
  client_name?: string;
  client_pan?: string;
  client_phone?: string;
  for_assesse_name?: string;
  for_assesse_pan?: string;
  self_assessment?: boolean | string;
  category_name?: string;
  sub_category_name?: string;
  owner?: { owner?: string };
  created_by?: string;
  [key: string]: unknown;
}

function isUuidLike(s: unknown): boolean {
  if (typeof s !== "string") return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s.trim());
}

export { isUuidLike };

export function getCaseApiNameFieldCandidates(fields: {
  name?: string;
  case_name?: string;
  case_title?: string;
  title?: string;
}): string[] {
  return [fields.name, fields.case_name, fields.case_title, fields.title]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);
}

export function resolveCaseNameFromApiFields(fields: {
  name?: string;
  case_name?: string;
  case_title?: string;
  title?: string;
  case_id_exp?: string;
}): string {
  for (const candidate of getCaseApiNameFieldCandidates(fields)) {
    if (!isUuidLike(candidate)) return candidate;
  }
  const ref = String(fields.case_id_exp ?? "").trim();
  if (ref) return ref;
  const uuidOnly = getCaseApiNameFieldCandidates(fields)[0];
  return uuidOnly ?? "";
}

export function normalizeCaseApiName(fields: {
  name?: string;
  case_name?: string;
  case_title?: string;
  title?: string;
  description?: string;
  case_id_exp?: string;
}): string {
  const fromNameFields = resolveCaseNameFromApiFields(fields);
  if (fromNameFields && !isUuidLike(fromNameFields)) return fromNameFields;

  const desc = String(fields.description ?? "").trim();
  const ref = String(fields.case_id_exp ?? "").trim();

  if (desc) return desc;
  if (ref) return ref;
  if (fromNameFields) return fromNameFields;
  return "—";
}

export function resolveCaseDisplayName(fields: {
  name?: string;
  case_id_exp?: string;
  description?: string;
}): string {
  return normalizeCaseApiName(fields);
}

function toDescriptionString(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value !== null && "detail" in value) {
    const d = (value as { detail?: unknown }).detail;
    return d !== null && d !== undefined ? String(d) : "";
  }
  return String(value);
}

function firstNonBlankString(...candidates: unknown[]): string | undefined {
  for (const v of candidates) {
    if (v === null || v === undefined) continue;
    const s = String(v).trim();
    if (s.length > 0) return s;
  }
  return undefined;
}

export function mapCaseDetailsToCaseDetailData(
  details: Record<string, unknown>,
  caseId: string
): CaseDetailData {
  const rawName = (details.name ?? details.case_name ?? details.case_title ?? details.title) as
    | string
    | undefined;
  const descVal = toDescriptionString(details.description);
  const caseName = resolveCaseNameFromApiFields({
    name: rawName,
    case_id_exp: details.case_id_exp as string | undefined,
  });
  const { description: _desc, ...restDetails } = details;
  const createdDateNormalized = firstNonBlankString(
    details.created_date,
    details.created_at,
    (details as Record<string, unknown>).createdDate,
    (details as Record<string, unknown>).created_on,
    (details as Record<string, unknown>).date_created,
    (details as Record<string, unknown>).case_created_date
  );
  return {
    ...restDetails,
    case_id: String(details.case_id ?? caseId),
    case_id_exp: details.case_id_exp as string,
    name: caseName === "—" ? "" : caseName,
    description: descVal,
    service: (details.service ?? details.service_name) as string,
    service_id: (() => {
      const raw =
        details.service_id ?? details.category_id ?? details.serviceId ?? details.categoryId;
      if (raw === undefined || raw === null) return undefined;
      const s = String(raw).trim();
      return s === "" ? undefined : s;
    })(),
    process_name: details.process_name as string,
    process_id: details.process_id as string,
    assessment_year: details.assessment_year as string,
    priority: details.priority as string,
    case_curr_state: (details.case_curr_state ?? details.status) as string,
    created_date: createdDateNormalized,
    author_name: (details.author_name ?? details.created_by) as string,
    client_id: details.client_id as string,
    client_name: details.client_name as string,
    client_pan: details.client_pan as string,
    client_phone: details.client_phone as string,
    category_name: (details.category_name ?? details.category) as string,
    sub_category_name: (details.sub_category_name ?? details.sub_category) as string,
    owner: details.owner as { owner?: string },
    created_by:
      details.created_by !== undefined && details.created_by !== null
        ? String(details.created_by)
        : undefined,
  };
}

export interface CaseStepTaskItem {
  item_id?: number;
  item?: string;
  item_type?: string;
  is_active?: boolean;
  is_compulsory?: boolean;
  todolist_id?: number;
  todolist_name?: string;
  todolist_decription?: string;
  is_checked?: boolean;
  is_user_added?: boolean;
  user_checked?: string | null;
  checked_by_name?: string | null;
}

export interface CaseStepTaskDetailsInfo {
  total_tasks?: number;
  completed?: number;
}

export interface CaseStepInfo {
  process?: string;
  process_id?: number;
  step_id?: number;
  name?: string;
  description?: string;
  review?: boolean;
  is_start?: boolean;
  is_end?: boolean;
  is_branch_step?: boolean;
  no_of_branches?: number;
  is_parallel?: boolean;
  todolist_id?: number;
  tasks?: CaseStepTaskItem[];
}

export interface CaseStepState {
  state_name?: string;
  state_shortcode?: string;
  step_name?: string;
  step_state_id?: number;
}

export interface CaseStepInfoItem {
  step_info?: CaseStepInfo;
  is_completed?: boolean;
  is_current?: boolean;
  task_details?: {
    case_step_todolist?: CaseStepTaskItem[];
    case_step_todolist_info?: CaseStepTaskDetailsInfo;
  };
  due_date?: string | null;
  assigned_users?: unknown[];
  current_state?: CaseStepState;
  case_step_state_log?: unknown[];
}

export interface GetCaseStepsPayload extends CasesSessionArgs {
  case_id: string | number;
}

export interface StepInOrderItem {
  process?: string;
  process_id?: number;
  step_id?: number;
  name?: string;
  description?: string;
  review?: boolean;
  is_start?: boolean;
  is_end?: boolean;
  is_branch_step?: boolean;
  no_of_branches?: number;
  is_parallel?: boolean;
  todolist_id?: number;
  tasks?: CaseStepTaskItem[];
}

export interface GetCaseStepsResponse {
  success?: boolean;
  message?: string;
  data?: {
    case_step_info?: CaseStepInfoItem[];
    case_step_log?: unknown[];
    all_steps?: StepInOrderItem[];
    steps_in_order?: StepInOrderItem[];
  };
  errors?: string[];
  error_code?: number | null;
  status_code?: number;
  meta?: {
    timestamp?: string;
    version?: string;
    request_id?: string;
    [key: string]: unknown;
  };
}

export interface CaseStepStateItem {
  step_name?: string;
  step_id?: number;
  step_state_id?: number;
  state_id?: number;
  state_name?: string;
  state_shortcode?: string;
  is_init?: boolean;
  is_accept?: boolean;
  is_reject?: boolean;
  is_hold?: boolean;
  is_current?: boolean;
}

export interface GetCaseStepStatesPayload extends CasesSessionArgs {
  case_id: string | number;
  step_id: string | number;
}

export interface GetCaseStepStatesResponse {
  success?: boolean;
  data?: { step_states?: CaseStepStateItem[] };
  errors?: string[];
  error_code?: number | null;
}

export interface UpdateCaseNotePayload extends CasesSessionArgs {
  case_id: string | number;
  note: string;
  is_deleted?: boolean;
}

export interface UpdateCaseNoteResponse {
  success?: boolean;
  data?: { note_info?: CaseNoteItem };
  errors?: string | string[] | { detail?: string };
}

export interface CaseNoteItem {
  note_id?: string | number;
  note?: string;
  created?: string;
  user?: string;
  user_full_name?: string;
  case_id?: string | number;
}

export interface GetCaseNotesPayload extends CasesSessionArgs {
  case_id: string | number;
}

export interface GetCaseNotesResponse {
  success?: boolean;
  data?: { case_notes?: CaseNoteItem[] };
  errors?: string | { detail?: string };
}

function getCaseNotes(payload: GetCaseNotesPayload): Promise<GetCaseNotesResponse> {
  const { business_id, device_token, session_id, case_id } = payload;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    case_id === null ||
    case_id === undefined ||
    case_id === ""
  ) {
    return Promise.reject("business_id, device_token, session_id and case_id are required");
  }
  return post<GetCaseNotesResponse>(api.GET_CASE_NOTES, payload);
}

export interface CaseActivityItem {
  message_id?: string | number;
  message?: string;
  user?: string;
  user_full_name?: string;
  case_id?: string | number;
  created?: string;
}

export interface GetCaseActivityPayload extends CasesSessionArgs {
  case_id: string | number;
}

export interface GetCaseActivityResponse {
  success?: boolean;
  data?: { case_activity?: CaseActivityItem[] };
  errors?: string | string[] | { detail?: string };
}

function getCaseActivity(payload: GetCaseActivityPayload): Promise<GetCaseActivityResponse> {
  const { business_id, device_token, session_id, case_id } = payload;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    case_id === null ||
    case_id === undefined ||
    case_id === ""
  ) {
    return Promise.reject("business_id, device_token, session_id and case_id are required");
  }
  return post<GetCaseActivityResponse>(api.GET_CASE_ACTIVITY, payload);
}

export interface CaseMessageItem {
  message_id?: string | number;
  message?: string;
  created?: string;
  user?: string;
  user_full_name?: string;
  case_id?: string | number;
}

export interface GetCaseMessagesPayload extends CasesSessionArgs {
  case_id: string | number;
}

export interface GetCaseMessagesResponse {
  success?: boolean;
  data?: { case_activity?: CaseMessageItem[] };
  errors?: string | string[] | { detail?: string };
}

function getCaseMessages(payload: GetCaseMessagesPayload): Promise<GetCaseMessagesResponse> {
  const { business_id, device_token, session_id, case_id } = payload;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    case_id === null ||
    case_id === undefined ||
    case_id === ""
  ) {
    return Promise.reject("business_id, device_token, session_id and case_id are required");
  }
  return post<GetCaseMessagesResponse>(api.GET_CASE_MESSAGES, payload);
}

export interface UpdateCaseMessagePayload extends CasesSessionArgs {
  case_id: string | number;
  message: string;
  is_deleted?: boolean;
}

export interface UpdateCaseMessageResponse {
  success?: boolean;
  data?: { comments_info?: CaseMessageItem };
  errors?: string | string[] | { detail?: string };
}

function updateCaseMessage(payload: UpdateCaseMessagePayload): Promise<UpdateCaseMessageResponse> {
  const { business_id, device_token, session_id, case_id, message } = payload;
  if (!business_id || !device_token || !session_id || !case_id || message === undefined) {
    return Promise.reject(
      "business_id, device_token, session_id, case_id and message are required"
    );
  }
  return post<UpdateCaseMessageResponse>(api.UPDATE_CASE_MESSAGE, payload);
}

function getCaseDetails(payload: GetCaseDetailsPayload): Promise<GetCaseDetailsResponse> {
  const { business_id, device_token, session_id, case_id } = payload;
  if (!business_id || !device_token || !session_id || !case_id) {
    return Promise.reject("business_id, device_token, session_id and case_id are required");
  }
  return post<GetCaseDetailsResponse>(api.GET_CASE_DETAILS, payload);
}

function getCaseSteps(payload: GetCaseStepsPayload): Promise<GetCaseStepsResponse> {
  const { business_id, device_token, session_id, case_id } = payload;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    case_id === null ||
    case_id === undefined ||
    case_id === ""
  ) {
    return Promise.reject("business_id, device_token, session_id and case_id are required");
  }
  return post<GetCaseStepsResponse>(api.GET_CASE_STEPS, {
    ...payload,
    case_id: typeof case_id === "number" ? case_id : Number(case_id),
  });
}

function getCaseStepStates(payload: GetCaseStepStatesPayload): Promise<GetCaseStepStatesResponse> {
  const { business_id, device_token, session_id, case_id, step_id } = payload;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    case_id === null ||
    case_id === undefined ||
    case_id === "" ||
    step_id === null ||
    step_id === undefined ||
    step_id === ""
  ) {
    return Promise.reject(
      "business_id, device_token, session_id, case_id and step_id are required"
    );
  }
  return post<GetCaseStepStatesResponse>(api.GET_CASE_STEP_STATES, {
    business_id,
    device_token,
    session_id,
    case_id: typeof case_id === "number" ? case_id : Number(case_id),
    step_id: typeof step_id === "number" ? step_id : Number(step_id),
  });
}

function updateCaseNote(payload: UpdateCaseNotePayload): Promise<UpdateCaseNoteResponse> {
  const { business_id, device_token, session_id, case_id } = payload;
  if (!business_id || !device_token || !session_id || !case_id) {
    return Promise.reject("business_id, device_token, session_id and case_id are required");
  }
  return post<UpdateCaseNoteResponse>(api.UPDATE_CASE_NOTE, payload);
}

export interface CreateCasePayload extends CasesSessionArgs {
  client_id?: string | number;
  name: string;
  description?: string;
  service_id?: string | number;
  assessment_year?: string;
  self_assessment?: boolean | string;
  assesse_id?: string;
  assesse_name?: string;
  assesse_email?: string;
  assesse_pan?: string;
  assesse_phone?: string;
  assesse_country_code?: string;
  created_by?: string;
  is_batch?: boolean;
  is_quick_create?: boolean;
  is_draft?: boolean;
}

export interface CreateCaseResponse {
  success?: boolean;
  data?: { case_info?: { case_id?: string | number; service_id?: string | number } };
  errors?: string;
}

function createCase(payload: CreateCasePayload): Promise<CreateCaseResponse> {
  const { business_id, device_token, session_id } = payload;
  if (!business_id || !device_token || !session_id) {
    return Promise.reject("business_id, device_token and session_id are required");
  }
  const body: Record<string, unknown> = { ...payload };

  if (typeof business_id === "string" && business_id.trim() !== "") {
    const n = Number(business_id);
    if (!Number.isNaN(n)) body.business_id = n;
  }
  if (
    payload.service_id !== undefined &&
    payload.service_id !== null &&
    payload.service_id !== ""
  ) {
    const raw = payload.service_id;
    const n = typeof raw === "number" ? raw : Number(String(raw).trim());
    body.service_id = Number.isNaN(n) ? raw : n;
  }

  if (typeof payload.self_assessment === "string") {
    const s = payload.self_assessment.trim().toLowerCase();
    if (s === "true") body.self_assessment = true;
    else if (s === "false") body.self_assessment = false;
  } else if (payload.self_assessment === true || payload.self_assessment === false) {
    body.self_assessment = payload.self_assessment;
  }

  const createdBy = typeof payload.created_by === "string" ? payload.created_by.trim() : "";
  const isClientCreated = createdBy === "client";

  const legacyAssesseeToCanonical = {
    assessee_id: "assesse_id",
    assessee_name: "assesse_name",
    assessee_email: "assesse_email",
    assessee_pan: "assesse_pan",
    assessee_phone: "assesse_phone",
    assessee_country_code: "assesse_country_code",
  } as const;
  Object.entries(legacyAssesseeToCanonical).forEach(([legacyKey, canonicalKey]) => {
    const canonicalValue = body[canonicalKey];
    const legacyValue = body[legacyKey];
    const hasCanonical =
      canonicalValue !== undefined &&
      canonicalValue !== null &&
      !(typeof canonicalValue === "string" && canonicalValue.trim() === "");
    const hasLegacy =
      legacyValue !== undefined &&
      legacyValue !== null &&
      !(typeof legacyValue === "string" && legacyValue.trim() === "");
    if (!hasCanonical && hasLegacy) {
      body[canonicalKey] = legacyValue;
    }
  });

  if (isClientCreated) {
    const assesseKeys = [
      "assesse_id",
      "assesse_name",
      "assesse_email",
      "assesse_pan",
      "assesse_phone",
      "assesse_country_code",
    ] as const;
    assesseKeys.forEach((k) => {
      const v = (payload as unknown as Record<string, unknown>)[k];
      const isEmptyString = typeof v === "string" && v.trim() === "";
      if (v === undefined || v === null || (isEmptyString && k !== "assesse_id")) {
        delete body[k];
      }
    });
  } else {
    body.assesse_id = payload.assesse_id ?? "";
    body.assesse_name = payload.assesse_name ?? "";
    body.assesse_email = payload.assesse_email ?? "";
    body.assesse_pan = payload.assesse_pan ?? "";
    body.assesse_phone = payload.assesse_phone ?? "";
    body.assesse_country_code = payload.assesse_country_code ?? "";
  }

  const assesseeKeys = [
    "assessee_id",
    "assessee_name",
    "assessee_email",
    "assessee_pan",
    "assessee_phone",
    "assessee_country_code",
  ] as const;
  assesseeKeys.forEach((k) => delete body[k]);

  if (createdBy !== "") {
    body.created_by = createdBy;
  } else {
    body.created_by = "business";
  }
  return post<CreateCaseResponse>(api.CREATE_CASE, body);
}

export interface BulkCreateCasesPayload extends CasesSessionArgs {
  client_groups?: (string | number)[];
  service_id?: string | number;
  assessment_year?: string;
  case_name?: string;
  case_desc?: string;
  case_description?: string;
  is_batch?: boolean;
  is_quick_create?: boolean;
  self_assessment?: boolean | string;
  created_by?: string;
  is_draft?: boolean;
}

export interface BulkCreateCasesResponse {
  success?: boolean;
  data?: { case_ids?: (string | number)[]; [key: string]: unknown };
  errors?: string;
}

function bulkCreateCases(payload: BulkCreateCasesPayload): Promise<BulkCreateCasesResponse> {
  const { business_id, device_token, session_id } = payload;
  if (!business_id || !device_token || !session_id) {
    return Promise.reject("business_id, device_token and session_id are required");
  }
  const body: Record<string, unknown> = { ...payload };
  if (typeof business_id === "string" && business_id.trim() !== "") {
    const n = Number(business_id);
    if (!Number.isNaN(n)) body.business_id = n;
  }
  if (
    payload.service_id !== undefined &&
    payload.service_id !== null &&
    payload.service_id !== ""
  ) {
    const raw = payload.service_id;
    const n = typeof raw === "number" ? raw : Number(String(raw).trim());
    body.service_id = Number.isNaN(n) ? raw : n;
  }
  if (body.case_desc === undefined && typeof payload.case_description === "string") {
    body.case_desc = payload.case_description;
  }
  if (body.is_batch === undefined) body.is_batch = true;
  if (body.is_quick_create === undefined) body.is_quick_create = false;
  return post<BulkCreateCasesResponse>(api.BULK_CASE_CREATION, body);
}

export interface UpdateCasePayload extends CasesSessionArgs {
  case_id: string;
  name?: string;
  description?: string;
  service_id?: string | number;
  assessment_year?: string;
  priority?: string;
}

export interface UpdateCaseResponse {
  success?: boolean;
  data?: { case_info?: Record<string, unknown> };
  errors?: string;
}

function updateCase(payload: UpdateCasePayload): Promise<UpdateCaseResponse> {
  const { business_id, device_token, session_id, case_id } = payload;
  if (!business_id || !device_token || !session_id || !case_id) {
    return Promise.reject("business_id, device_token, session_id and case_id are required");
  }
  return post<UpdateCaseResponse>(api.UPDATE_CASE, payload);
}

export interface UpdateCaseStatusPayload extends CasesSessionArgs {
  case_id: string;
  case_status: string;
  case_alert_message?: string;
}

export interface UpdateCaseStatusResponse {
  success?: boolean;
  errors?: string | string[];
  data?: {
    case_status?: {
      case_status_saved?: boolean;
      case_curr_state?: string;
    };
  };
}

function updateCaseStatus(payload: UpdateCaseStatusPayload): Promise<UpdateCaseStatusResponse> {
  const { business_id, device_token, session_id, case_id, case_status } = payload;
  if (!business_id || !device_token || !session_id || !case_id || !case_status) {
    return Promise.reject(
      "business_id, device_token, session_id, case_id and case_status are required"
    );
  }
  const normalizedStatus = case_status === "revert-to-draft" ? "reject-case" : case_status;
  return post<UpdateCaseStatusResponse>(api.UPDATE_CASE_STATUS, {
    ...payload,
    case_status: normalizedStatus,
  });
}

export interface DeleteCasePayload extends CasesSessionArgs {
  case_id: string | number;
  comments?: string;
}

export interface DeleteCaseResponse {
  success?: boolean;
  errors?: string | string[];
}

export interface CheckCaseGroupShortcodePayload extends CasesSessionArgs {
  case_group_shortcode: string;
}

export interface CheckCaseGroupShortcodeResponse {
  success?: boolean;
  message?: string;
  data?: {
    case_group_info?: {
      is_valid?: boolean;
    };
  };
  errors?: string | string[] | null;
}

function checkCaseGroupShortcode(
  payload: CheckCaseGroupShortcodePayload
): Promise<CheckCaseGroupShortcodeResponse> {
  const { business_id, device_token, session_id, case_group_shortcode } = payload;
  if (!business_id || !device_token || !session_id) {
    return Promise.reject("business_id, device_token and session_id are required");
  }
  const trimmed = case_group_shortcode.trim();
  if (!trimmed) {
    return Promise.reject("case_group_shortcode is required");
  }
  const biz =
    typeof business_id === "string" && business_id.trim() !== ""
      ? Number(business_id)
      : business_id;
  return postAllowHttpError<CheckCaseGroupShortcodeResponse>(api.CHECK_CASE_GROUP_SHORTCODE, {
    business_id: typeof biz === "number" && !Number.isNaN(biz) ? biz : business_id,
    case_group_shortcode: trimmed,
    device_token,
    session_id,
  });
}

function deleteCase(payload: DeleteCasePayload): Promise<DeleteCaseResponse> {
  const { business_id, device_token, session_id, case_id, comments } = payload;
  if (!business_id || !device_token || !session_id || case_id === undefined || case_id === null) {
    return Promise.reject("business_id, device_token, session_id and case_id are required");
  }
  const biz =
    typeof business_id === "string" && business_id.trim() !== ""
      ? Number(business_id)
      : business_id;
  const body: Record<string, unknown> = {
    business_id: typeof biz === "number" && !Number.isNaN(biz) ? biz : business_id,
    device_token,
    session_id,
    case_id: typeof case_id === "number" ? case_id : Number(case_id),
    comments: comments ?? "",
  };
  return post<DeleteCaseResponse>(api.DELETE_CASE, body);
}

export interface GetCaseTagsPayload extends CasesSessionArgs {
  case_id: string | number;
}

export interface CaseTagItem {
  tag_id?: string | number;
  tag_name?: string;
  tag_color?: string;
  color?: string;
  name?: string;
  case_id?: string | number;
  case_name?: string;
  [key: string]: unknown;
}

export interface GetCaseTagsResponse {
  success?: boolean;
  data?: { case_tags?: CaseTagItem[] };
  errors?: string | unknown[];
}

function getCaseTags(payload: GetCaseTagsPayload): Promise<GetCaseTagsResponse> {
  const { business_id, device_token, session_id, case_id } = payload;
  if (!business_id || !device_token || !session_id || case_id === undefined || case_id === null) {
    return Promise.reject("business_id, device_token, session_id and case_id are required");
  }
  return post<GetCaseTagsResponse>(api.GET_CASE_TAGS, payload);
}

export interface UpdateCaseTagsPayload extends CasesSessionArgs {
  case_id: string | number;
  tags: number[];
}

export interface UpdateCaseTagsResponse {
  success?: boolean;
  data?: { case_tags?: { tags_saved?: boolean } };
  errors?: string | unknown[];
}

function updateCaseTags(payload: UpdateCaseTagsPayload): Promise<UpdateCaseTagsResponse> {
  const { business_id, device_token, session_id, case_id, tags } = payload;
  if (!business_id || !device_token || !session_id || !case_id) {
    return Promise.reject("business_id, device_token, session_id and case_id are required");
  }
  return post<UpdateCaseTagsResponse>(api.UPDATE_CASE_TAGS, {
    business_id,
    device_token,
    session_id,
    case_id: Number(case_id),
    tags: Array.isArray(tags) ? tags.map(Number) : [],
  });
}

export interface GetCaseAssignmentPayload extends CasesSessionArgs {
  case_id: string;
}

export interface GetCaseAssignmentResponse {
  success?: boolean;
  data?: { owner?: string; owner_id?: string | number; [key: string]: unknown };
  errors?: string;
}

function getCaseAssignment(payload: GetCaseAssignmentPayload): Promise<GetCaseAssignmentResponse> {
  const { business_id, device_token, session_id, case_id } = payload;
  if (!business_id || !device_token || !session_id || !case_id) {
    return Promise.reject("business_id, device_token, session_id and case_id are required");
  }
  return post<GetCaseAssignmentResponse>(api.GET_CASE_ASSIGNMENT, payload);
}

export interface AssignCasePayload extends CasesSessionArgs {
  case_id: string | number;
  owner_id: string | number;
  assigned_by_id: string | number;
  comments?: string;
}

export interface AssignCaseResponse {
  success?: boolean;
  data?: { case_assignment?: Record<string, unknown> };
  errors?: string | string[];
}

function assignCase(payload: AssignCasePayload): Promise<AssignCaseResponse> {
  const { business_id, device_token, session_id, case_id, owner_id, assigned_by_id } = payload;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    case_id === undefined ||
    case_id === null ||
    owner_id === undefined ||
    owner_id === null ||
    assigned_by_id === undefined ||
    assigned_by_id === null
  ) {
    return Promise.reject(
      "business_id, device_token, session_id, case_id, owner_id and assigned_by_id are required"
    );
  }
  const body: Record<string, unknown> = {
    business_id,
    device_token,
    session_id,
    case_id: typeof case_id === "number" ? case_id : Number(case_id),
    owner_id: typeof owner_id === "number" ? owner_id : Number(owner_id),
    assigned_by_id: typeof assigned_by_id === "number" ? assigned_by_id : Number(assigned_by_id),
    comments: payload.comments ?? "",
  };
  return post<AssignCaseResponse>(api.ASSIGN_CASE, body);
}

export interface CaseDocumentTypeItem {
  doc_type_id?: number;
  doc_type_name?: string;
  description?: string;
  file_type?: string;
  allowed_types?: string;
  file_size?: number;
  doc_type_tag?: string;
  is_default?: boolean;
  is_active?: boolean;
  is_optional?: boolean;
  user_documents?: CaseUploadedDocumentItem[];
  user_uploaded?: CaseUploadedDocumentItem[];
}

export interface CaseUploadedDocumentFileItem {
  id?: number | null;
  document?: string;
  original_filename?: string;
  sort_order?: number;
}

export interface CaseUploadedDocumentItem {
  case_document_id?: number;
  case_doc_id?: number;
  user_document_id?: number;
  document_id?: number;
  document?: string;
  doc_name?: string;
  file_size?: string;
  file_count?: number;
  files?: CaseUploadedDocumentFileItem[];
  doc_type_id?: number;
  doc_type_name?: string;
  is_protected?: boolean;
  password?: string;
  uploaded_by?: string;
  uploaded_on?: string;
  biz_view_only?: boolean;
  [key: string]: unknown;
}

export interface GetCaseUploadedDocumentsPayload extends CasesSessionArgs {
  case_id: string | number;
  include_upload_meta?: boolean;
}

export interface GetCaseUploadedDocumentsResponse {
  success?: boolean;
  data?: { case_documents?: CaseDocumentTypeItem[] };
  errors?: string | string[];
}

function getCaseUploadedDocuments(
  payload: GetCaseUploadedDocumentsPayload
): Promise<GetCaseUploadedDocumentsResponse> {
  const { case_id, business_id, device_token, session_id, include_upload_meta } = payload;
  if (!case_id || !business_id || !device_token || !session_id) {
    return Promise.reject("case_id, business_id, device_token and session_id are required");
  }
  const body: Record<string, unknown> = { case_id, business_id, device_token, session_id };
  if (include_upload_meta === true) {
    body.include_upload_meta = true;
  }
  return post<GetCaseUploadedDocumentsResponse>(api.GET_CASE_UPLOADED_DOCUMENTS, body);
}

export interface GetCaseDocumentsPayload extends CasesSessionArgs {
  case_id: string | number;
  stage?: string;
}

export interface GetCaseDocumentsResponse {
  success?: boolean;
  data?: { case_documents?: CaseDocumentTypeItem[] };
  errors?: string | string[];
}

function getCaseDocuments(payload: GetCaseDocumentsPayload): Promise<GetCaseDocumentsResponse> {
  const { case_id, business_id, device_token, session_id } = payload;
  if (!case_id || !business_id || !device_token || !session_id) {
    return Promise.reject("case_id, business_id, device_token and session_id are required");
  }
  return post<GetCaseDocumentsResponse>(api.GET_CASE_DOCUMENTS, payload);
}

export interface DocTypeItem {
  document_type_id: number;
  name: string;
  description?: string;
  service?: string;
  service_id?: number;
  file_type?: string;
  allowed_types?: string;
  file_size?: number;
  is_default?: boolean;
  is_optional?: boolean;
  is_active?: boolean;
  doc_type_tag?: string;
  [key: string]: unknown;
}

export interface GetAllDocTypesPayload extends CasesSessionArgs {}

export interface GetAllDocTypesResponse {
  success?: boolean;
  data?: { doc_types?: DocTypeItem[] };
  errors?: string | string[];
}

function getAllDocTypes(payload: GetAllDocTypesPayload): Promise<GetAllDocTypesResponse> {
  const { business_id, device_token, session_id } = payload;
  if (!business_id || !device_token || !session_id) {
    return Promise.reject("business_id, device_token and session_id are required");
  }
  return post<GetAllDocTypesResponse>(api.GET_ALL_DOC_TYPES, payload);
}

export interface GetServiceDocTypesPayload extends CasesSessionArgs {
  service_id: string | number;
}

export interface GetServiceDocTypesResponse {
  success?: boolean;
  data?: { doc_types?: DocTypeItem[]; document_types?: DocTypeItem[] } | DocTypeItem[] | null;
  errors?: string | string[];
}

export function isDocTypeActiveFlag(value: unknown): boolean {
  if (
    value === false ||
    value === 0 ||
    value === "0" ||
    value === "false" ||
    value === "False" ||
    value === "N" ||
    value === "no"
  ) {
    return false;
  }
  return true;
}

export function extractDocTypesArrayFromGetServiceResponse(
  res: GetServiceDocTypesResponse | null | undefined
): unknown[] {
  const data = res?.data;
  if (data === null || data === undefined) return [];
  if (Array.isArray(data)) return data;
  if (typeof data !== "object") return [];
  const d = data as Record<string, unknown>;
  const keys = [
    "doc_types",
    "document_types",
    "service_doc_types",
    "docTypes",
    "documentTypes",
    "case_document_types",
    "doctype_info",
    "types",
  ];
  for (const key of keys) {
    const v = d[key];
    if (Array.isArray(v)) return v;
  }
  return [];
}

export function normalizeDocTypeItem(raw: unknown): DocTypeItem {
  if (!raw || typeof raw !== "object") {
    return { document_type_id: 0, name: "" };
  }
  const r = raw as Record<string, unknown>;
  const idRaw = r.document_type_id ?? r.doctype_id;
  const numId = typeof idRaw === "number" ? idRaw : Number(String(idRaw ?? ""));
  const fileSizeRaw = r.file_size;
  const fileSizeNum =
    typeof fileSizeRaw === "number"
      ? fileSizeRaw
      : fileSizeRaw !== undefined && fileSizeRaw !== null && String(fileSizeRaw).trim() !== ""
        ? Number(String(fileSizeRaw))
        : undefined;
  return {
    document_type_id: Number.isFinite(numId) && !Number.isNaN(numId) ? numId : 0,
    name: String(r.name ?? ""),
    description: r.description !== undefined ? String(r.description) : undefined,
    service:
      r.service !== undefined
        ? String(r.service)
        : r.service_name !== undefined
          ? String(r.service_name)
          : undefined,
    service_id:
      typeof r.service_id === "number"
        ? r.service_id
        : r.service_id !== undefined && r.service_id !== null
          ? Number(r.service_id)
          : undefined,
    file_type: r.file_type !== undefined ? String(r.file_type) : undefined,
    allowed_types: r.allowed_types !== undefined ? String(r.allowed_types) : undefined,
    file_size: fileSizeNum !== undefined && Number.isFinite(fileSizeNum) ? fileSizeNum : undefined,
    is_default: r.is_default === true,
    is_optional: r.is_optional === true,
    is_active: isDocTypeActiveFlag(r.is_active),
    doc_type_tag: r.doc_type_tag !== undefined ? String(r.doc_type_tag) : undefined,
  };
}

function getServiceDocTypes(
  payload: GetServiceDocTypesPayload
): Promise<GetServiceDocTypesResponse> {
  const { service_id, business_id, device_token, session_id } = payload;
  if (
    service_id === undefined ||
    service_id === null ||
    service_id === "" ||
    !device_token ||
    !session_id
  ) {
    return Promise.reject("service_id, device_token and session_id are required");
  }
  const raw = service_id;
  const n = typeof raw === "number" ? raw : Number(String(raw).trim());
  const sid = Number.isFinite(n) && !Number.isNaN(n) ? n : raw;
  if (!business_id) {
    return post<GetServiceDocTypesResponse>(api.GET_PLATFORM_DOC_TYPES, {
      service_id: String(raw).trim(),
      device_token,
      session_id,
    });
  }
  return post<GetServiceDocTypesResponse>(api.GET_SERVICE_DOC_TYPES, {
    business_id,
    device_token,
    session_id,
    service_id: sid,
  });
}

export interface CreateDocTypePayload extends CasesSessionArgs {
  service_id: string | number;
  document_type_id?: number;
  name: string;
  description?: string;
  doc_type_tag: string;
  file_type?: string;
  allowed_types?: string;
  file_size?: number;
  is_default?: boolean;
  is_optional?: boolean;
  is_active?: boolean;
}

export interface CreateDocTypeResponse {
  success?: boolean;
  data?: {
    document_type?: DocTypeItem;
    doc_types?: DocTypeItem[];
    doctype_info?: DocTypeItem & { doctype_id?: number; doctype_created?: boolean };
  };
  errors?: string | string[];
}

function createDocType(payload: CreateDocTypePayload): Promise<CreateDocTypeResponse> {
  const { service_id, business_id, device_token, session_id } = payload;
  if (
    service_id === undefined ||
    service_id === null ||
    service_id === "" ||
    !device_token ||
    !session_id
  ) {
    return Promise.reject("service_id, device_token and session_id are required");
  }
  if (!business_id) {
    const fileSizeRaw = payload.file_size;
    const fileSizeStr =
      fileSizeRaw === undefined || fileSizeRaw === null ? "" : String(fileSizeRaw);
    const platformBody = {
      service_id: Number(service_id),
      name: String(payload.name ?? "").trim(),
      description: String(payload.description ?? "").trim(),
      doctype_id:
        payload.document_type_id !== undefined && payload.document_type_id !== null
          ? String(payload.document_type_id)
          : "",
      file_type: payload.file_type ?? "",
      allowed_types: payload.allowed_types ?? payload.file_type ?? "",
      file_size: fileSizeStr,
      doc_type_tag: payload.doc_type_tag,
      is_default: Boolean(payload.is_default),
      is_optional: Boolean(payload.is_optional),
      is_active: Boolean(payload.is_active),
      device_token,
      session_id,
    };
    return post<CreateDocTypeResponse>(api.CREATE_PLATFORM_DOC_TYPES, platformBody);
  }
  const body: Record<string, unknown> = {
    ...payload,
    service_id: Number(service_id),
  };
  if (payload.document_type_id !== undefined && payload.document_type_id !== null) {
    body.doc_type_id = payload.document_type_id;
    delete body.document_type_id;
  }
  return post<CreateDocTypeResponse>(api.CREATE_DOC_TYPE, body);
}

export interface UploadUserDocumentPayload extends CasesSessionArgs {
  case_id: string | number;
  doc_type_id: string | number;
  doc_no?: string;
  is_protected?: boolean;
  password?: string;
  document: string;
  uuid: string;
  biz_view_only?: boolean;
}

export interface UploadUserDocumentResponse {
  success?: boolean;
  data?: {
    file_uploaded?: boolean;
    file_url?: string;
    case_document_id?: number;
    doc_no?: string;
    case_id?: number;
  };
  errors?: string | string[];
}

function uploadUserDocument(
  payload: UploadUserDocumentPayload
): Promise<UploadUserDocumentResponse> {
  const { case_id, document, uuid, business_id, device_token, session_id } = payload;
  if (!case_id || !document || !uuid || !business_id || !device_token || !session_id) {
    return Promise.reject(
      "case_id, document, uuid, business_id, device_token and session_id are required"
    );
  }
  return post<UploadUserDocumentResponse>(api.UPLOAD_USER_DOCUMENT, {
    ...payload,
    doc_type_id: String(payload.doc_type_id),
  });
}

export interface BulkUploadUserDocumentItem {
  document: string;
  uuid: string;
  doc_no?: string;
  original_filename?: string;
  sort_order?: number;
  is_protected?: boolean;
  password?: string;
  biz_view_only?: boolean;
}

export interface BulkUploadUserDocumentPayload extends CasesSessionArgs {
  case_id: string | number;
  doc_type_id: string | number;
  documents: BulkUploadUserDocumentItem[];
  doc_no?: string;
  /** When set, API creates one bundled document (head + files). */
  doc_name?: string;
  is_protected?: boolean;
  password?: string;
  biz_view_only?: boolean;
}

export interface BulkUploadUserDocumentResultItem {
  document?: string;
  uuid?: string;
  success?: boolean;
  case_document_id?: number;
  file_url?: string;
  doc_no?: string;
  idempotent?: boolean;
  errors?: string[];
}

export interface BulkUploadUserDocumentResponse {
  success?: boolean;
  data?: {
    case_id?: number;
    case_document_id?: number;
    document_id?: number;
    doc_name?: string;
    file_count?: number;
    succeeded?: number;
    failed?: number;
    results?: BulkUploadUserDocumentResultItem[];
  };
  errors?: string | string[];
  error_code?: number;
}

export interface DownloadCaseDocumentZipPayload extends CasesSessionArgs {
  case_doc_id: string | number;
}

export interface DownloadCaseDocumentZipResponse {
  success?: boolean;
  data?: {
    download_url?: string;
    file_name?: string;
    file_count?: number;
    case_doc_id?: number;
  };
  errors?: string | string[];
  error_code?: number;
}

function downloadCaseDocumentZip(
  payload: DownloadCaseDocumentZipPayload
): Promise<DownloadCaseDocumentZipResponse> {
  const { case_doc_id, business_id, device_token, session_id } = payload;
  if (
    case_doc_id === undefined ||
    case_doc_id === null ||
    case_doc_id === "" ||
    !business_id ||
    !device_token ||
    !session_id
  ) {
    return Promise.reject("case_doc_id, business_id, device_token and session_id are required");
  }
  return post<DownloadCaseDocumentZipResponse>(api.DOWNLOAD_CASE_DOCUMENT_ZIP, payload);
}

function bulkUploadUserDocument(
  payload: BulkUploadUserDocumentPayload
): Promise<BulkUploadUserDocumentResponse> {
  const { case_id, documents, business_id, device_token, session_id } = payload;
  if (
    !case_id ||
    !business_id ||
    !device_token ||
    !session_id ||
    !Array.isArray(documents) ||
    documents.length === 0
  ) {
    return Promise.reject(
      "case_id, documents, business_id, device_token and session_id are required"
    );
  }
  return post<BulkUploadUserDocumentResponse>(api.BULK_UPLOAD_USER_DOCUMENT, {
    ...payload,
    doc_type_id: String(payload.doc_type_id),
  });
}

export interface DeleteCaseDocumentPayload extends CasesSessionArgs {
  case_id: string | number;
  case_document_id?: number;
  user_document_id?: number;
  doctype_id?: string | number;
  delete_comments?: string;
}

export interface DeleteCaseDocumentResponse {
  success?: boolean;
  errors?: string | string[];
}

function deleteCaseDocument(
  payload: DeleteCaseDocumentPayload
): Promise<DeleteCaseDocumentResponse> {
  const { case_id, business_id, device_token, session_id } = payload;
  const docId = payload.user_document_id ?? payload.case_document_id;
  const doctypeId = payload.doctype_id;
  if (
    case_id === undefined ||
    case_id === null ||
    case_id === "" ||
    !business_id ||
    !device_token ||
    !session_id ||
    docId === undefined ||
    docId === null ||
    doctypeId === undefined ||
    doctypeId === null ||
    doctypeId === ""
  ) {
    return Promise.reject(
      "case_id, user_document_id (or case_document_id), doctype_id, business_id, device_token and session_id are required"
    );
  }
  const caseIdNum = Number(case_id);
  const userDocNum = Number(docId);
  const doctypeNum = Number(doctypeId);
  if (!Number.isFinite(userDocNum) || !Number.isFinite(doctypeNum)) {
    return Promise.reject("user_document_id and doctype_id must be valid numbers");
  }
  const body: Record<string, unknown> = {
    case_id: Number.isFinite(caseIdNum) ? caseIdNum : case_id,
    user_document_id: userDocNum,
    doctype_id: doctypeNum,
    delete_comments: (payload.delete_comments ?? "").trim(),
    business_id: typeof business_id === "number" ? business_id : Number(business_id),
    device_token,
    session_id,
  };
  return post<DeleteCaseDocumentResponse>(api.DELETE_CASE_DOCUMENT, body);
}

export interface EditCaseDocumentPayload extends CasesSessionArgs {
  case_id: string | number;
  document_id: number;
  case_doc_id: number;
  biz_view_only: boolean;
}

export interface EditCaseDocumentResponse {
  success?: boolean;
  errors?: string | string[];
}

function editCaseDocument(payload: EditCaseDocumentPayload): Promise<EditCaseDocumentResponse> {
  const {
    case_id,
    document_id,
    case_doc_id,
    biz_view_only,
    business_id,
    device_token,
    session_id,
  } = payload;
  if (
    case_id === undefined ||
    case_id === null ||
    case_id === "" ||
    document_id === undefined ||
    document_id === null ||
    case_doc_id === undefined ||
    case_doc_id === null ||
    !business_id ||
    !device_token ||
    !session_id
  ) {
    return Promise.reject(
      "case_id, document_id, case_doc_id, business_id, device_token and session_id are required"
    );
  }
  const caseIdNum = Number(case_id);
  const documentIdNum = Number(document_id);
  const caseDocIdNum = Number(case_doc_id);
  if (!Number.isFinite(documentIdNum) || !Number.isInteger(documentIdNum)) {
    return Promise.reject("document_id must be a valid integer");
  }
  if (!Number.isFinite(caseDocIdNum) || !Number.isInteger(caseDocIdNum)) {
    return Promise.reject("case_doc_id must be a valid integer");
  }
  const businessIdNum = Number(business_id);
  const body: Record<string, unknown> = {
    business_id: Number.isFinite(businessIdNum) ? businessIdNum : business_id,
    case_id: Number.isFinite(caseIdNum) ? caseIdNum : case_id,
    document_id: documentIdNum,
    case_doc_id: caseDocIdNum,
    biz_view_only,
    device_token,
    session_id,
  };
  return post<EditCaseDocumentResponse>(api.EDIT_CASE_DOCUMENT, body);
}

export interface SwitchProcessPayload extends CasesSessionArgs {
  case_id: string | number;
  process_id: string | number;
}

export interface SwitchProcessResponse {
  success?: boolean;
  errors?: string;
}

function switchProcess(payload: SwitchProcessPayload): Promise<SwitchProcessResponse> {
  const { business_id, device_token, session_id, case_id, process_id } = payload;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    case_id === undefined ||
    case_id === null ||
    process_id === undefined ||
    process_id === null
  ) {
    return Promise.reject(
      "business_id, device_token, session_id, case_id and process_id are required"
    );
  }
  return post<SwitchProcessResponse>(api.SWITCH_PROCESS, {
    business_id,
    device_token,
    session_id,
    case_id: typeof case_id === "number" ? case_id : Number(case_id),
    process_id: typeof process_id === "number" ? process_id : Number(process_id),
  });
}

export interface UpdateCaseStepStatePayload extends CasesSessionArgs {
  case_id: string | number;
  step_state_id: string | number;
  step_id?: string | number;
}

export interface UpdateCaseStepStateResponse {
  success?: boolean;
  data?: {
    case_state?: {
      case_state_saved?: boolean;
      case_state_id?: number;
      current_state?: string;
      current_state_id?: number;
      case?: string;
      case_id?: number;
    };
    move_step?: {
      move_to_next_step?: boolean;
    };
  };
  errors?: string | string[];
  error_code?: string | null;
}

function updateCaseStepState(
  payload: UpdateCaseStepStatePayload
): Promise<UpdateCaseStepStateResponse> {
  const { business_id, device_token, session_id, case_id, step_state_id, step_id } = payload;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    case_id === undefined ||
    case_id === null ||
    step_state_id === undefined ||
    step_state_id === null
  ) {
    return Promise.reject(
      "business_id, device_token, session_id, case_id and step_state_id are required"
    );
  }
  const body: Record<string, unknown> = {
    business_id: typeof business_id === "number" ? business_id : Number(business_id),
    device_token,
    session_id,
    case_id: typeof case_id === "number" ? case_id : Number(case_id),
    step_state_id: typeof step_state_id === "number" ? step_state_id : Number(step_state_id),
  };
  if (step_id !== undefined && step_id !== null && step_id !== "") {
    body.step_id = typeof step_id === "number" ? step_id : Number(step_id);
  }
  return post<UpdateCaseStepStateResponse>(api.UPDATE_CASE_STEP_STATE, body);
}

export interface SwitchCaseStepPayload extends CasesSessionArgs {
  case_id: string | number;
  step_id: string | number;
}

export interface SwitchCaseStepResponse {
  success?: boolean;
  errors?: string | string[];
}

function switchCaseStep(payload: SwitchCaseStepPayload): Promise<SwitchCaseStepResponse> {
  const { business_id, device_token, session_id, case_id, step_id } = payload;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    case_id === undefined ||
    case_id === null ||
    step_id === undefined ||
    step_id === null
  ) {
    return Promise.reject(
      "business_id, device_token, session_id, case_id and step_id are required"
    );
  }
  return post<SwitchCaseStepResponse>(api.SWITCH_CASE_STEP, {
    business_id,
    device_token,
    session_id,
    case_id: typeof case_id === "number" ? case_id : Number(case_id),
    step_id: typeof step_id === "number" ? step_id : Number(step_id),
  });
}

export interface UpdateCaseStepTodolistItemPayload extends CasesSessionArgs {
  case_id: string | number;
  item_id: number;
  is_checked: boolean;
  item_type?: string;
}

export interface UpdateCaseStepTodolistItemResponse {
  success?: boolean;
  data?: { case_todolog?: { todo_saved?: boolean } };
  errors?: string | string[];
  error_code?: string | null;
}

function updateCaseStepTodolistItem(
  payload: UpdateCaseStepTodolistItemPayload
): Promise<UpdateCaseStepTodolistItemResponse> {
  const { business_id, device_token, session_id, case_id, item_id, is_checked, item_type } =
    payload;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    case_id === undefined ||
    case_id === null ||
    item_id === undefined ||
    item_id === null
  ) {
    return Promise.reject(
      "business_id, device_token, session_id, case_id and item_id are required"
    );
  }
  return post<UpdateCaseStepTodolistItemResponse>(api.UPDATE_CASE_STEP_TODOLIST_ITEM, {
    business_id: typeof business_id === "number" ? business_id : Number(business_id),
    device_token,
    session_id,
    case_id: typeof case_id === "number" ? case_id : Number(case_id),
    item_id,
    is_checked,
    item_type: item_type ?? "business",
  });
}

export interface CaseQuestionOption {
  option_id?: number;
  option?: string;
  option_value?: string;
  option_text?: string;
  selected?: boolean;
  [key: string]: unknown;
}

export interface CaseQuestionItem {
  question_id: number;
  title?: string;
  question_type?: string;
  level?: number;
  is_active?: boolean;
  service?: string;
  is_default?: boolean;
  is_optional?: boolean;
  is_mandatory?: boolean;
  mandatory?: boolean;
  required?: boolean;
  is_required?: boolean;
  options?: CaseQuestionOption[] | string[];
  answer?: string | boolean | number | null;
  single_answer?: string | null;
  boolean_ans?: string | boolean | null;
  text_answer?: string | null;
  multi_answer?: unknown;
  [key: string]: unknown;
}

export interface GetCaseQuestionsPayload extends CasesSessionArgs {
  case_id: string | number;
  level: number;
}

export interface GetCaseQuestionsResponse {
  success?: boolean;
  data?: { question_info?: CaseQuestionItem[] };
  errors?: string | string[];
  error_code?: string | null;
}

function getCaseQuestions(payload: GetCaseQuestionsPayload): Promise<GetCaseQuestionsResponse> {
  const { business_id, device_token, session_id, case_id, level } = payload;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    case_id === null ||
    case_id === undefined ||
    case_id === ""
  ) {
    return Promise.reject("business_id, device_token, session_id and case_id are required");
  }
  return post<GetCaseQuestionsResponse>(api.GET_CASE_QUESTIONS, {
    business_id: typeof business_id === "number" ? business_id : Number(business_id),
    device_token,
    session_id,
    case_id: typeof case_id === "number" ? case_id : Number(case_id),
    level: Number(level),
  });
}

export interface QuestionAnswerPayloadItem {
  question_id: number;
  boolean_ans: string | boolean;
  text_answer: string;
  single_answer: string | number;
  multi_answer: Array<{ option: string; option_id: string | number }>;
}

export interface AddUpdateCaseAnswersPayload extends CasesSessionArgs {
  case_id: string | number;
  question_answers: QuestionAnswerPayloadItem[];
}

export interface AddUpdateCaseAnswersResponse {
  success?: boolean;
  data?: { case_answers?: { answers_saved?: boolean } };
  errors?: string | string[];
  error_code?: string | null;
}

function addUpdateCaseAnswers(
  payload: AddUpdateCaseAnswersPayload
): Promise<AddUpdateCaseAnswersResponse> {
  const { business_id, device_token, session_id, case_id, question_answers } = payload;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    case_id === null ||
    case_id === undefined ||
    case_id === ""
  ) {
    return Promise.reject("business_id, device_token, session_id and case_id are required");
  }
  return post<AddUpdateCaseAnswersResponse>(api.ADD_UPDATE_CASE_ANSWERS, {
    business_id: typeof business_id === "number" ? business_id : Number(business_id),
    device_token,
    session_id,
    case_id: typeof case_id === "number" ? case_id : Number(case_id),
    question_answers,
  });
}

export interface CreateUserTodoPayload extends CasesSessionArgs {
  todolist_id: number;
  item: string;
  is_active: boolean;
  todo_id?: number | null;
}

export interface CreateUserTodoResponse {
  success?: boolean;
  data?: {
    user_todo_item?: {
      user_todo_list_item?: string;
      user_todo_id?: number;
      user_todolist_id?: number;
      username?: string;
      name?: string;
      description?: string;
    };
  };
  errors?: string | string[];
  error_code?: string | null;
}

function createUserTodo(payload: CreateUserTodoPayload): Promise<CreateUserTodoResponse> {
  const { business_id, device_token, session_id, todolist_id, item, is_active, todo_id } = payload;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    todolist_id === null ||
    todolist_id === undefined
  ) {
    return Promise.reject("business_id, device_token, session_id and todolist_id are required");
  }
  const body: Record<string, unknown> = {
    business_id: typeof business_id === "number" ? business_id : Number(business_id),
    device_token,
    session_id,
    todolist_id,
    item,
    is_active,
  };
  if (todo_id !== null && todo_id !== undefined) body.todo_id = todo_id;
  return post<CreateUserTodoResponse>(api.CREATE_USER_TODO, body);
}

export const casesService = {
  getCases,
  getClientCases,
  getSearchLog,
  searchCases,
  getCaseDetails,
  getCaseSteps,
  getCaseStepStates,
  getCaseNotes,
  getCaseActivity,
  getCaseMessages,
  updateCaseNote,
  updateCaseMessage,
  createCase,
  bulkCreateCases,
  updateCase,
  updateCaseStatus,
  getCaseTags,
  updateCaseTags,
  getCaseAssignment,
  assignCase,
  getCaseUploadedDocuments,
  getCaseDocuments,
  getAllDocTypes,
  getServiceDocTypes,
  createDocType,
  uploadUserDocument,
  bulkUploadUserDocument,
  downloadCaseDocumentZip,
  deleteCaseDocument,
  editCaseDocument,
  deleteCase,
  checkCaseGroupShortcode,
  switchProcess,
  switchCaseStep,
  updateCaseStepState,
  updateCaseStepTodolistItem,
  getCaseQuestions,
  addUpdateCaseAnswers,
  createUserTodo,
};
